import React, { useEffect, useState, useContext } from 'react'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import Button from '../formfields/Button'
import { GlobalContext } from '../GlobalContext'
import FooterNavButtons from '../shared-components/FooterNavButtons'
import HeaderDetails from '../shared-components/HeaderDetails'
import Card from '../ui-elements/Card'
import RedAlert from '../ui-elements/RedAlert'
import Spinner from '../ui-elements/Spinner'
import SummaryCheckWindow from './SummaryCheckWindow'
import { getChecklistProgress } from '../utils/getChecklistProgress'
import { subscribe } from '../utils/pubsub'
import Modal from '../ui-elements/Modal'
import YellowAlert from '../ui-elements/YellowAlert'
import GreenAlert from '../ui-elements/GreenAlert'
import ChecklistStatus from './ChecklistStatus'
import QuestionSummaryDetail from './QuestionSummaryDetail'
import { sortAllQuestionAnswersForCheckPeriod } from '../utils/sortAllQuestionAnswersForCheckPeriod'
import { calculateRepeatAnswers } from '../utils/calculateRepeatAnswers'
import AddButton from '../formfields/AddButton'
import { canUserAddExtraAnswers } from '../utils/findNextQuestionUrl'


type Props = {
    checkPeriodId: string
    thisChecklist: ObjectStringKeyAnyValue ,
    questionFormat?: 'compact' | 'full'
}

function LoopThroughQuestions({
    checkPeriodId,
    thisChecklist,
    questionFormat
}: Props) {

    const {
        tableData,
        setShowModal,
        isSignoffRequired,
        checkPeriodIdsForActiveCheckists,
        sendMessageToWebsocket,
        checkIfThereAreQuestionsAndAnswersForChecklistPeriodId
    } = useContext(GlobalContext)

    const navigate = useNavigate()
    const [thisChecklistQuestions, setThisChecklistQuestions] = useState<null | Array<ObjectStringKeyAnyValue>>(null)
    const [showNotFound, setShowNotFound] = useState(false)
    const [sortedQuestionsAndAnswers, setSortedQuestionsAndAnswers] = useState<null | Array<ObjectStringKeyAnyValue>>(null)
    const thisCheckPeriod = checkPeriodId && tableData && tableData.ChecklistPeriods && tableData.ChecklistPeriods[checkPeriodId]

    useEffect(() => {
        checkIfThereAreQuestionsAndAnswersForChecklistPeriodId(checkPeriodId)
    }, [])


    useEffect(() => {
        //console.log(`🚨 running update`)
        if (checkPeriodId && tableData) {
            if (
                tableData.Checklists
                && tableData.ChecklistPeriods
                && tableData.ChecklistPeriods[checkPeriodId]
                && tableData.ChecklistPeriods[checkPeriodId]['ChecklistId']
                && tableData.Checklists[tableData.ChecklistPeriods[checkPeriodId]['ChecklistId']]
            ) {


                const allQuestionAnswersArray = sortAllQuestionAnswersForCheckPeriod(checkPeriodId, tableData)
                setSortedQuestionsAndAnswers(allQuestionAnswersArray)

                // get questions for this checklist
                // filter follow up questions
                // and sort by order
                if (tableData['ChecklistQuestions']) {
                    let questionsArray: any[] = []
                    for (const parentQuestionId in tableData['ChecklistQuestions']) {
                        const parentQuestion = tableData['ChecklistQuestions'] && tableData['ChecklistQuestions'][parentQuestionId]
                        if (
                            parentQuestion && parentQuestion.ChecklistId   && parentQuestion.ChecklistId  === thisChecklist.Id &&
                            (!parentQuestion['parentQuestionId'] && !parentQuestion['parentActionId'])
                        ) {
                            questionsArray.push(parentQuestion)

                            for (const followUpQuestionId in tableData['ChecklistQuestions']) {
                                const followUpQuestion = tableData['ChecklistQuestions'][followUpQuestionId]
                                if (
                                    followUpQuestion.ChecklistId === thisChecklist.Id &&
                                    followUpQuestion['parentQuestionId'] === parentQuestionId
                                ) {
                                    questionsArray.push(followUpQuestion)

                                }
                            }

                        }
                    }

                    questionsArray.sort((a, b) => parseInt(a.QuestionOrder) - parseInt(b.QuestionOrder))
                    setThisChecklistQuestions(questionsArray)
                    setShowNotFound(false)
                }

            } else {
                setShowNotFound(true)
            }
        }
    }, [checkPeriodId, tableData])





    const AddAnswerToQuestion = (questionId: string, checkPeriodId: string) => {
        setShowModal({ "spinner": 'Adding answer...' })
        const payload: ObjectStringKeyAnyValue = {
            action: "checklistsClientApp",
            subAction: "addAnswerObjectToQuestion",
            questionId: `${questionId}`,
            checkPeriodId: `${checkPeriodId}`
        }
        const unsubscribe = subscribe("answerObjectAddedToQuestion", data => {
            setShowModal(null)
            unsubscribe()
        })
        sendMessageToWebsocket(JSON.stringify(payload))
    }

    const isThisTheLastRepetitionForQuestion = (thisAnswer: ObjectStringKeyAnyValue, sortedQuestionsAndAnswers: any) => {

        let highestRepNumber = 0
        for (const answer of sortedQuestionsAndAnswers) {
            if (answer.QuestionId === thisAnswer.QuestionId) {
                const thisAnswerRep = answer.repeatAnswerAfter && answer.repeatAnswerAfter.number ? answer.repeatAnswerAfter.number : 0
                if (thisAnswerRep > highestRepNumber) {
                    highestRepNumber = thisAnswerRep
                    //console.log(`highestRepNumber: ${highestRepNumber}`)
                }
            }
        }
        const thisAnswerRepNumber = parseInt(thisAnswer.repeatAnswerAfter && thisAnswer.repeatAnswerAfter.number ? thisAnswer.repeatAnswerAfter.number : 0)

        if (thisAnswerRepNumber === highestRepNumber) {
            //  console.log(`✅ thisAnswerRepNumber: ${thisAnswerRepNumber} highestRepNumber: ${highestRepNumber}`)
            return true
        }
        //   console.log(`⛔️ thisAnswerRepNumber: ${thisAnswerRepNumber} highestRepNumber: ${highestRepNumber}`)
        return false
    }

    return <div className={`w-full flex flex-col gap-2`}>

        {checkPeriodId && sortedQuestionsAndAnswers && sortedQuestionsAndAnswers.map((answer: ObjectStringKeyAnyValue, index: number) => {
            const questionsFromTableData = tableData && tableData.ChecklistQuestions
            const questionId = answer.QuestionId
            const question = questionsFromTableData && questionId && questionsFromTableData[questionId]
            if(!question) {
                return null
            }

            const parentQuestion = question && question.parentQuestionId && tableData.ChecklistQuestions && tableData.ChecklistQuestions[question.parentQuestionId]
            const answersForThisQuestion = question && sortedQuestionsAndAnswers.filter((answer: ObjectStringKeyAnyValue) => answer.QuestionId === question.Id)

            
            const canUserAddAnswers = canUserAddExtraAnswers(answer.Id, sortedQuestionsAndAnswers, tableData)
            const isThisTheLastRepetition = isThisTheLastRepetitionForQuestion(answer, sortedQuestionsAndAnswers)
            let answer_number = 1
            if (answer && answer.repeatAnswerAfter && answer.repeatAnswerAfter.number) {
                answer_number = parseInt(answer.repeatAnswerAfter.number) + 1
            }
           
            return <div className={`flex flex-col gap-2 w-full`} key={index}>
                <QuestionSummaryDetail
                    key={index}
                    questionAnswer={answer}
                    checkPeriodId={checkPeriodId}
                    question={question}
                    answerNumber={answer_number}
                    answersForThisQuestion={answersForThisQuestion}
                    sortedQuestionsAndAnswers={sortedQuestionsAndAnswers}
                    parentQuestion={parentQuestion}
                    questionFormat={questionFormat}
                />
                {parentQuestion && canUserAddAnswers && isThisTheLastRepetition && <AddButton
                    onClick={() => AddAnswerToQuestion(parentQuestion.Id, checkPeriodId)}
                    text={`Add another answer for this question`}
                />}
                {!parentQuestion && canUserAddAnswers && isThisTheLastRepetition && <AddButton
                    onClick={() => AddAnswerToQuestion(question.Id, checkPeriodId)}
                    text={`Add another answer for this question`}
                />}
            </div>
        })}

    </div>
}



export default LoopThroughQuestions